import {
  CheckCircle2,
  XCircle,
  X,
  ExternalLink,
  FolderOpen,
  GraduationCap,
  ShieldCheck,
  Award,
  Link as LinkIcon,
  FileText,
  CircleSlash,
} from 'lucide-react';
import type { ValidateResult } from '../hooks/useApprenantActions.js';

interface Props {
  result: ValidateResult;
  onClose: () => void;
}

/**
 * Modal de récapitulatif après validation d'une identité.
 * Montre ce qui a été créé / connecté en cascade dans tous les modules.
 */
export function ValidationReceiptModal({ result, onClose }: Props) {
  const { receipt, modulesConnectes, isTest } = result;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-200 bg-emerald-50 px-5 py-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg font-bold text-emerald-900">Identité validée</h2>
            {isTest && (
              <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                🧪 TEST
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-emerald-700 hover:bg-emerald-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Récap des 4 actions */}
        <div className="space-y-2 p-5">
          {/* DRIVE */}
          <ReceiptRow
            ok={receipt.drive.ok}
            icon={<FolderOpen className="h-4 w-4" />}
            title={
              receipt.drive.fiscalYear
                ? `Dossier Google Drive · ${receipt.drive.fiscalYear.label}`
                : 'Dossier Google Drive'
            }
          >
            {receipt.drive.ok && receipt.drive.folderUrl ? (
              <div>
                {receipt.drive.fiscalYear && (
                  <div className="mb-0.5 text-[10px] font-semibold text-emerald-700">
                    📅 Exercice fiscal : {receipt.drive.fiscalYear.range}
                  </div>
                )}
                <div className="text-[11px] text-neutral-600">
                  Dossier créé avec 5 sous-dossiers (Convention, Attestations, Émargements,
                  Certificat, Autres)
                </div>
                <a
                  href={receipt.drive.folderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 mt-1 inline-flex items-center gap-1 text-[11px] font-semibold hover:underline"
                >
                  Ouvrir le dossier <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ) : (
              <div className="text-[11px] text-rose-600">{receipt.drive.error}</div>
            )}
          </ReceiptRow>

          {/* PDF EDOF source — visible si on a un PDF en attente */}
          {receipt.pdfEdof && !receipt.pdfEdof.skipped && (
            <ReceiptRow
              ok={receipt.pdfEdof.ok}
              icon={<FileText className="h-4 w-4" />}
              title="PDF EDOF source"
            >
              {receipt.pdfEdof.ok && receipt.pdfEdof.fileUrl ? (
                <div>
                  <div className="text-[11px] text-neutral-600">
                    Uploadé dans <span className="font-medium">01 - Convention</span> /{' '}
                    <span className="font-mono text-[10px]">{receipt.pdfEdof.fileName}</span>
                  </div>
                  <a
                    href={receipt.pdfEdof.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 mt-1 inline-flex items-center gap-1 text-[11px] font-semibold hover:underline"
                  >
                    Ouvrir le PDF <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              ) : receipt.pdfEdof.ok ? (
                <div className="text-[11px] text-neutral-600">
                  Déjà rattaché au dossier à la création
                  {receipt.pdfEdof.fileName ? (
                    <>
                      {' '}
                      : <span className="font-mono text-[10px]">{receipt.pdfEdof.fileName}</span>
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="text-[11px] text-rose-600">{receipt.pdfEdof.error}</div>
              )}
            </ReceiptRow>
          )}
          {receipt.pdfEdof?.skipped && (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-2.5">
              <div className="flex items-start gap-2">
                <CircleSlash className="mt-0.5 h-4 w-4 text-neutral-400" />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-neutral-700">
                    <FileText className="h-4 w-4" />
                    PDF EDOF source
                  </div>
                  <div className="mt-1 text-[11px] italic text-neutral-500">
                    {receipt.pdfEdof.error ?? 'Aucun PDF EDOF rattaché à ce dossier.'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* DOCS AUTO-GÉNÉRÉS depuis Templates Google Docs */}
          {receipt.docs && (receipt.docs.generated.length > 0 || receipt.docs.error) && (
            <ReceiptRow
              ok={receipt.docs.ok}
              icon={<FileText className="h-4 w-4" />}
              title={`Docs générés (${receipt.docs.generated.filter((d) => !d.error).length}/${receipt.docs.generated.length})`}
            >
              {receipt.docs.error ? (
                <div className="text-[11px] text-rose-600">{receipt.docs.error}</div>
              ) : (
                <ul className="space-y-0.5 text-[11px]">
                  {receipt.docs.generated.map((d) => (
                    <li key={d.templateName} className="flex items-start gap-1.5">
                      {d.error ? (
                        <XCircle className="mt-0.5 h-3 w-3 flex-shrink-0 text-rose-500" />
                      ) : d.filled ? (
                        <CheckCircle2 className="mt-0.5 h-3 w-3 flex-shrink-0 text-emerald-600" />
                      ) : (
                        <FileText className="mt-0.5 h-3 w-3 flex-shrink-0 text-neutral-400" />
                      )}
                      <div className="flex-1">
                        <span className="font-medium text-neutral-800">{d.templateName}</span>
                        <span className="ml-1 text-[10px] text-neutral-500">→ {d.subfolder}</span>
                        {d.filled && (
                          <span className="ml-1 rounded bg-emerald-100 px-1 py-0 text-[9px] font-semibold uppercase text-emerald-700">
                            auto-rempli
                          </span>
                        )}
                        {!d.filled && !d.error && (
                          <span className="ml-1 rounded bg-neutral-100 px-1 py-0 text-[9px] font-semibold uppercase text-neutral-600">
                            squelette
                          </span>
                        )}
                        {d.fileUrl && (
                          <a
                            href={d.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 ml-2 inline-flex items-center gap-0.5 text-[10px] font-semibold hover:underline"
                          >
                            ouvrir <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        )}
                        {d.error && (
                          <div className="text-[10px] italic text-rose-600">{d.error}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </ReceiptRow>
          )}

          {/* MOODLE */}
          <ReceiptRow
            ok={receipt.moodle.ok || receipt.moodle.status === 'skipped'}
            icon={<GraduationCap className="h-4 w-4" />}
            title="Compte Moodle"
          >
            <div className="space-y-0.5 text-[11px]">
              {/* Statut RÉEL Moodle — distinct selon ce qui s'est vraiment passé */}
              <div className="font-medium text-neutral-800">
                {receipt.moodle.status === 'created' && (
                  <span className="text-emerald-700">🆕 User Moodle créé</span>
                )}
                {receipt.moodle.status === 'found' && (
                  <span className="text-emerald-700">
                    ↻ User Moodle existant retrouvé et réutilisé
                  </span>
                )}
                {receipt.moodle.status === 'not_found' && (
                  <span className="text-amber-700">
                    ⚠ User Moodle introuvable et auto-provision désactivé
                  </span>
                )}
                {receipt.moodle.status === 'skipped' && (
                  <span className="text-neutral-600">⊘ Accès Moodle pas encore ouvert</span>
                )}
                {receipt.moodle.status === 'error' && (
                  <span className="text-rose-700">✗ Erreur Moodle</span>
                )}
                {receipt.moodle.userId && (
                  <span className="ml-1 font-mono text-[10px] text-neutral-500">
                    (ID {receipt.moodle.userId})
                  </span>
                )}
              </div>
              {receipt.moodle.newlyEnrolledCourses.length > 0 && (
                <div className="text-neutral-600">
                  📚 Enrôlé dans {receipt.moodle.newlyEnrolledCourses.length} cours :{' '}
                  <span className="font-mono">
                    {receipt.moodle.newlyEnrolledCourses.join(', ')}
                  </span>
                </div>
              )}
              {receipt.moodle.status === 'created' || receipt.moodle.status === 'found' ? (
                receipt.moodle.emailSent ? (
                  <div className="text-neutral-600">📧 Email bienvenue envoyé</div>
                ) : (
                  <div className="italic text-neutral-500">
                    Email bienvenue non envoyé (mode TEST ou déjà existant)
                  </div>
                )
              ) : null}
              {receipt.moodle.error && receipt.moodle.status !== 'created' && (
                <div className="text-[10px] italic text-neutral-500">{receipt.moodle.error}</div>
              )}
            </div>
          </ReceiptRow>

          {/* QUALIOPI */}
          <ReceiptRow
            ok={receipt.qualiopi.ok}
            icon={<Award className="h-4 w-4" />}
            title={`Qualiopi (${receipt.qualiopi.formsIssued.length}/7 formulaires initialisés)`}
          >
            {receipt.qualiopi.formsIssued.length > 0 && (
              <div className="text-[11px] text-neutral-600">
                Initialisés : {receipt.qualiopi.formsIssued.join(', ')}
              </div>
            )}
            {receipt.qualiopi.errors.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-[10px] italic text-amber-700">
                {receipt.qualiopi.errors.map((e, i) => (
                  <li key={i}>· {e}</li>
                ))}
              </ul>
            )}
          </ReceiptRow>

          {/* COMPLIANCE FLAG */}
          <ReceiptRow
            ok={receipt.complianceFlag.ok}
            icon={<ShieldCheck className="h-4 w-4" />}
            title="Compliance Flag → Green (Audit Ready)"
          >
            {receipt.complianceFlag.error && (
              <div className="text-[11px] text-rose-600">{receipt.complianceFlag.error}</div>
            )}
          </ReceiptRow>
        </div>

        {/* Modules désormais connectés */}
        <div className="bg-primary-50 border-t border-neutral-100 px-5 py-3">
          <div className="text-primary-700 mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest">
            <LinkIcon className="h-3 w-3" />
            Modules désormais connectés à cet apprenant
          </div>
          <ul className="text-primary-900 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
            {modulesConnectes.map((m, i) => (
              <li key={i} className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                {m}
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-3">
          {receipt.drive.folderUrl && (
            <a
              href={receipt.drive.folderUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
            >
              Ouvrir le dossier Drive
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiptRow({
  ok,
  icon,
  title,
  children,
}: {
  ok: boolean;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-lg border p-2.5 ${
        ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-rose-200 bg-rose-50/40'
      }`}
    >
      <div className="flex items-start gap-2">
        <div className={ok ? 'text-emerald-600' : 'text-rose-600'}>
          {ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-neutral-900">
            {icon}
            {title}
          </div>
          {children && <div className="mt-1">{children}</div>}
        </div>
      </div>
    </div>
  );
}
